/**
 * Calendar View Component (react-big-calendar wrapper)
 */

import { Calendar as BigCalendar, Views, Event } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CalendarEvent } from '../../lib/api';
import { CalendarViewType, CalendarEventItem } from './types';
import { localizer, getEventStyleGetter } from './utils';

interface CalendarViewProps {
  events: CalendarEventItem[];
  view: CalendarViewType;
  date: Date;
  onEventClick: (event: CalendarEvent) => void;
  onSelectSlot: (slotInfo: { start: Date; end: Date }) => void;
  onNavigate: (date: Date) => void;
}

export function CalendarView({ events, view, date, onEventClick, onSelectSlot, onNavigate }: CalendarViewProps) {
  // Using key to force re-render when date or events change - this resets BigCalendar
  const calendarKey = `${view}-${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${events.length}`;

  // Build props object with date-related props that have incomplete types.
  // `key` is intentionally NOT included here because React 19 warns when a
  // `key` is spread with the rest of the props — it must be passed directly
  // on the JSX element.
  const calendarProps = {
    localizer,
    events,
    startAccessor: 'start' as const,
    endAccessor: 'end' as const,
    style: { height: '100%' },
    eventPropGetter: getEventStyleGetter,
    defaultView: view,
    defaultDate: date,
    onNavigate,
    views: [Views.MONTH, Views.WEEK, Views.DAY],
    toolbar: false,
    popup: true,
    selectable: true,
    tooltipAccessor: (event: Event) => {
      const e = event as unknown as CalendarEventItem;
      return e.resource?.description || '';
    },
    onSelectEvent: (event: Event) => {
      const e = event as unknown as CalendarEventItem;
      onEventClick(e.resource);
    },
    onSelectSlot,
  };

  return (
    <div style={{ height: '700px' }}>
      <BigCalendar key={calendarKey} {...calendarProps} />
    </div>
  );
}
